/**
 * Dashboard (`/`) — investigator zero-state homepage.
 *
 * Composition mirrors Foundry's zero-state home surface:
 *   1. Hero band   : isometric backdrop + logo + "Welcome back, X" + a
 *                    full-width global-search launcher (Cmd/Ctrl+J Omnibar).
 *   2. Action strip: one card, three columns split by dividers, with a thin
 *                    gradient top accent (Upload / Browse / Ask IRIS).
 *   3. Body grid   : left = Recent cases table (Name / Last updated), right =
 *                    stacked Applications + Get-in-touch cards.
 *   4. IRIS banner : dark gradient promo with suggested questions + an ask
 *                    box; everything opens the IRIS overlay.
 *
 * Only Blueprint primitives + existing Foundry `.bp-home-*` chrome. The search
 * launcher and IRIS entry points dispatch the same global keydowns the shell
 * listens for, so the dashboard doesn't thread props through PanelStack.
 */

import {
  useCallback,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { Link, useNavigate } from "react-router";
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogFooter,
  Icon,
  InputGroup,
  Intent,
  NonIdealState,
  Section,
  SectionCard,
  type IconName,
  type PanelProps,
} from "@blueprintjs/core";

import { useCases } from "../lib/hooks";
import { useAuth } from "../contexts/auth-context";
import { Page } from "../components/layout";
import type { DashboardPanelProps } from "../components/panels/types";
import { useRecentApps } from "../lib/recent-apps";
// Platform-aware omnibar shortcut label. Rendered as flat text (not the shared
// CmdKHint <Tag>) so the home search hint has no keycap rectangle.
const IS_MAC =
  typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/i.test(navigator.platform);

type RecentFilter = "all" | "flagged" | "mine";

const RECENT_FILTERS: { value: RecentFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "flagged", label: "Flagged" },
  { value: "mine", label: "Mine" },
];

// Suggested prompts on the IRIS banner. Clicking any of them opens the overlay.
const IRIS_SUGGESTIONS = [
  "Summarize this case",
  "Who are the key entities?",
  "Build a timeline",
];

export function Dashboard(_props: PanelProps<DashboardPanelProps>): ReactElement {
  const { data: cases = [], isPending: casesPending } = useCases();
  const { session } = useAuth();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<RecentFilter>("all");
  const [isNewCaseOpen, setIsNewCaseOpen] = useState(false);

  const firstName = useMemo(() => {
    const given = session?.givenName?.trim();
    if (given) return given;
    const fullName = session?.name?.trim();
    if (fullName) return fullName.split(/\s+/)[0] ?? fullName;
    const nick = session?.nickname?.trim();
    if (nick) return nick;
    const local = session?.email?.split("@")[0] ?? "";
    const tok = local.split(/[._\-+]/).filter(Boolean)[0] ?? "";
    if (!tok) return "";
    return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
  }, [session?.givenName, session?.name, session?.nickname, session?.email]);

  const visibleCases = useMemo(() => {
    const base = (() => {
      if (filter === "flagged") return cases.filter((c) => c.flagged);
      if (filter === "mine") {
        const lc = session?.email?.split("@")[0]?.toLowerCase() ?? "";
        return cases.filter((c) =>
          (c.leadDetective ?? "").toLowerCase().includes(lc),
        );
      }
      return [...cases];
    })();
    return base
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 10);
  }, [cases, filter, session?.email]);

  const { recentApps, recordLaunch } = useRecentApps();

  const openApplicationPortal = useCallback(() => {
    window.dispatchEvent(new CustomEvent("iron:open-application-portal"));
  }, []);

  // Global search + IRIS open via custom events the CommandPalette / AppShell
  // listen for. Synthesizing ⌘J / ⌘⇧U KeyboardEvents no longer works —
  // Blueprint's useHotkeys ignores synthetic key events.
  const openSearch = useCallback(() => {
    window.dispatchEvent(new CustomEvent("iron:open-search"));
  }, []);

  const openIris = useCallback(() => {
    window.dispatchEvent(new CustomEvent("iron:open-iris"));
  }, []);

  return (
    <Page data-testid="dashboard">
      {/* --- Hero band: isometric backdrop + welcome + search ---------- */}
      <div className="bp-home-hero-wrap">
        <div className="bp-home-hero-bg" aria-hidden="true" />
        <header className="bp-home-welcome-header">
          <span className="bp-home-welcome-logo" aria-hidden="true">
            <span className="bp-home-welcome-logo-ring">
              <svg width={26} height={26} viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2a3 3 0 0 1 3 3v1h2a3 3 0 0 1 3 3v9a3 3 0 0 1 -3 3h-14a3 3 0 0 1 -3 -3v-9a3 3 0 0 1 3 -3h2v-1a3 3 0 0 1 3 -3zm0 2h-4a1 1 0 0 0 -1 1v1h6v-1a1 1 0 0 0 -1 -1" />
              </svg>
            </span>
          </span>
          <h1 className="bp-home-welcome-text">
            {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
          </h1>
        </header>

        {/* Click-to-open launcher for the Cmd/Ctrl+J omnibar, rendered as a
            real <input type="search"> (readOnly) per Blueprint InputGroup. */}
        <InputGroup
          type="search"
          large
          fill
          readOnly
          data-testid="dashboard-search"
          className="bp-home-search-launcher"
          leftIcon="search"
          placeholder="Search for anything in your enterprise"
          aria-label="Search for anything in your enterprise (Cmd+J)"
          onClick={openSearch}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openSearch();
            }
          }}
          rightElement={
            <span className="bp-home-search-launcher-shortcut">{IS_MAC ? "⌘J" : "Ctrl+J"}</span>
          }
        />
      </div>

      {/* --- Action strip: single card, 3 columns --------------------- */}
      <section className="bp-home-action-strip">
        <div className="bp-home-action-strip-content">
          <StripCard
            icon="cloud-upload"
            title="Upload evidence"
            description="Drop a PDF, image, audio, or video into a case to start automatic extraction."
            primary={{ label: "Choose a case", onClick: () => navigate("/workspace/compass"), intent: Intent.PRIMARY }}
          />
          <span className="bp-home-action-strip-divider" aria-hidden="true" />
          <StripCard
            icon="folder-open"
            title="Browse cases"
            description="Jump into the case list and filter by status or lead detective."
            primary={{ label: "View cases", onClick: () => navigate("/workspace/compass"), rightIcon: "arrow-right" }}
            secondary={{ label: "New case", onClick: () => setIsNewCaseOpen(true) }}
          />
          <span className="bp-home-action-strip-divider" aria-hidden="true" />
          <StripCard
            icon="clean"
            title="Ask IRIS Assistant"
            description="Cite evidence, draft summaries, or query the case knowledge base."
            primary={{ label: "Open IRIS", onClick: openIris, rightIcon: "arrow-top-right" }}
          />
        </div>
      </section>

      {/* --- Body grid: Recent (left) + sidebar (right) --------------- */}
      <div className="bp-zsh-body">
        <section className="bp-home-section bp-home-section--shadow">
          <header className="bp-home-section-header">
            <h2 className="bp-home-section-title">Recent</h2>
            <div className="bp-home-recent-tabs">
              {RECENT_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  data-testid={`dashboard-filter-${f.value}`}
                  className={`bp-home-recent-tab${filter === f.value ? " is-active" : ""}`}
                  aria-pressed={filter === f.value}
                  onClick={() => setFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </header>

          <Card elevation={0} className="bp-home-recent-table">
            {casesPending ? (
              <div className="bp-zsh-recent-loading">
                <span className="bp6-skeleton" style={{ width: "100%", height: 28 }} />
                <span className="bp6-skeleton" style={{ width: "92%", height: 28 }} />
                <span className="bp6-skeleton" style={{ width: "96%", height: 28 }} />
                <span className="bp6-skeleton" style={{ width: "88%", height: 28 }} />
              </div>
            ) : visibleCases.length === 0 ? (
              <div className="bp-home-recent-table-empty">
                <NonIdealState
                  icon="folder-open"
                  title="No recent cases"
                  description="Cases appear here as they're updated."
                  action={<Link to="/workspace/compass" data-testid="dashboard-explore-cases" className="bp6-button bp6-intent-primary bp6-minimal">Explore cases</Link>}
                />
              </div>
            ) : (
              <div className="bp-home-recent-table-inner">
                <div className="bp-home-recent-table-header">
                  <div className="bp-home-recent-table-header-cell">Name</div>
                  <div className="bp-home-recent-table-header-cell">Last updated</div>
                </div>
                {visibleCases.map((c) => (
                  <Link
                    key={c.id}
                    to={`/cases/${c.id}`}
                    data-testid={`dashboard-recent-row-${c.id}`}
                    className="bp-home-recent-row bp-home-recent-row--link"
                  >
                    <div className="bp-home-recent-row-name-cell">
                      <span className="bp-home-recent-row-icon">
                        <Icon icon={c.flagged ? "flag" : "folder-close"} size={14} style={{ color: c.flagged ? "var(--bp-warning)" : "var(--bp-text-dim)" }} />
                      </span>
                      <span className="bp-home-recent-row-title bp6-text-overflow-ellipsis">{c.title}</span>
                    </div>
                    <div className="bp-home-recent-row-timestamp">{formatRelative(c.updatedAt)}</div>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </section>

        <aside className="bp-zsh-side">
          {/* Idiomatic Blueprint <Section> (title + header divider + card frame,
              themed automatically) — no hand-rolled card chrome. */}
          <Section
            title="Applications"
            compact
            rightElement={
              <Button
                data-testid="dashboard-view-all-apps"
                minimal
                intent={Intent.PRIMARY}
                small
                onClick={openApplicationPortal}
                aria-label="View all applications"
                text="View all"
              />
            }
          >
            <SectionCard padded>
              {recentApps.length === 0 ? (
                <div className="bp-home-apps-empty bp6-text-muted">
                  Launch an app and it will appear here.{" "}
                  <button type="button" data-testid="dashboard-apps-portal-inline" className="bp-zsh-inline-link" onClick={openApplicationPortal}>
                    Open the apps portal
                  </button>
                  .
                </div>
              ) : (
                <div className="bp-home-apps-grid-default">
                  {recentApps.map((app) => (
                    <a key={app.id} href={app.href} onClick={() => recordLaunch(app.id)} data-testid={`dashboard-app-tile-${app.id}`} className="bp-home-app-card-link">
                      <Card elevation={1} interactive className="bp-home-app-card-card">
                        <span className="bp-home-app-card-icon"><Icon icon={app.icon} size={28} /></span>
                      </Card>
                      <div className="bp-home-app-card-title">{app.name}</div>
                    </a>
                  ))}
                </div>
              )}
            </SectionCard>
          </Section>

          <Section title="Get in touch" compact>
            <SectionCard padded>
              <div className="bp-home-support-card-description bp6-text-muted">
                For platform, evidence-pipeline, or compliance questions.
              </div>
              <div className="bp-home-support-card-buttons">
                <Button data-testid="dashboard-contact-support" fill outlined intent={Intent.PRIMARY} icon="chat">Contact support</Button>
                <Button data-testid="dashboard-community" fill outlined intent={Intent.PRIMARY} icon="people" rightIcon="arrow-top-right">
                  Investigator community
                </Button>
              </div>
            </SectionCard>
          </Section>
        </aside>
      </div>

      {/* --- IRIS assistant banner ------------------------------------ */}
      <section
        className="bp-home-aip-promo-card"
        data-testid="dashboard-iris-banner"
        aria-label="Ask IRIS, your investigation assistant"
      >
        <h2 className="bp-home-aip-promo-title">Get help from IRIS, your investigation assistant</h2>
        <div className="bp-home-aip-promo-questions">
          {IRIS_SUGGESTIONS.map((q) => (
            <button
              key={q}
              type="button"
              data-testid="dashboard-iris-suggestion"
              className="bp-home-aip-promo-question"
              onClick={openIris}
            >
              <Icon icon="chat" size={12} />
              <span>{q}</span>
            </button>
          ))}
          <button
            type="button"
            data-testid="dashboard-iris-ask"
            className="bp-home-aip-promo-ask"
            onClick={openIris}
            aria-label="Ask IRIS a question"
          >
            Ask your question…
          </button>
        </div>
        <span className="bp-home-aip-promo-watermark" aria-hidden="true">
          <svg width={120} height={120} viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2a3 3 0 0 1 3 3v1h2a3 3 0 0 1 3 3v9a3 3 0 0 1 -3 3h-14a3 3 0 0 1 -3 -3v-9a3 3 0 0 1 3 -3h2v-1a3 3 0 0 1 3 -3zm0 2h-4a1 1 0 0 0 -1 1v1h6v-1a1 1 0 0 0 -1 -1" />
          </svg>
        </span>
      </section>

      <Dialog
        isOpen={isNewCaseOpen}
        onClose={() => setIsNewCaseOpen(false)}
        title="New case"
        icon="info-sign"
      >
        <DialogBody>
          Case creation API not yet available — use RMS import for now.
        </DialogBody>
        <DialogFooter actions={<Button data-testid="dashboard-new-case-close" text="Close" onClick={() => setIsNewCaseOpen(false)} />} />
      </Dialog>
    </Page>
  );
}

// --------------------------------------------------------------------------

interface StripButton {
  label: string;
  onClick: () => void;
  intent?: Intent;
  rightIcon?: IconName;
}

interface StripCardProps {
  icon: IconName;
  title: string;
  description: string;
  primary: StripButton;
  secondary?: { label: string; onClick: () => void };
}

function StripCard({ icon, title, description, primary, secondary }: StripCardProps): ReactElement {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <div data-testid={`dashboard-action-${slug}`} className="bp-home-action-card">
      <span className="bp-home-action-card-icon"><Icon icon={icon} size={22} /></span>
      <div className="bp-home-action-card-text">
        <div className="bp-home-action-card-title">{title}</div>
        <div className="bp-home-action-card-description">{description}</div>
        <div className="bp-home-action-card-buttons">
          <Button
            data-testid={`dashboard-action-${slug}-primary`}
            intent={primary.intent}
            outlined={primary.intent === undefined}
            small
            text={primary.label}
            rightIcon={primary.rightIcon}
            onClick={primary.onClick}
          />
          {secondary && (
            <Button data-testid={`dashboard-action-${slug}-secondary`} minimal small text={secondary.label} onClick={secondary.onClick} />
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const m = Math.round(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toISOString().slice(0, 10);
}
